import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Archive, Loader2, RotateCcw, Search, Store, Upload, XCircle } from 'lucide-react'

import {
  getProduction,
  listAssetTags,
  listAssets,
  listProductionListings,
  type Production,
  type ProductionAsset,
  type StudioProductionListing,
  patchProductionAsset,
  registerProductionAsset,
  revokeStudioAssetListing,
  uploadProductionAsset,
} from '@/api/studio-production'
import { CATALOG_LIST_GRID_CLASS } from '@/shared/components/catalog/catalogListGrid'
import { CatalogListPageShell } from '@/shared/components/catalog/CatalogListPageShell'
import { CatalogListSkeleton } from '@/shared/components/catalog/CatalogListSkeleton'
import { CatalogListToolbar } from '@/shared/components/catalog/CatalogListToolbar'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { AssetListingModal } from '@/features/studio/modals/AssetListingModal'
import { AssetCard } from '@/features/studio/pages/assets/AssetCard'
import { assetMatchesMediaTab, isRegisteredAsset, isVisibleAssetTag, type AssetMediaTab } from '@/features/studio/pages/assets/asset-utils'
import { ProductionEmptyPanel, ProtoBtn, ProtoChip, ProtoInput, ProtoPagination, ProtoTabs } from '@/features/studio/components/ui-kit'
import { useClientPagination } from '@/features/studio/hooks/useClientPagination'

const REVOCABLE_LISTING_STATUSES = new Set(['draft', 'rejected', 'pending_platform', 'published'])

const ASSETS_PAGE_SIZE = 20

function isRevocableListing(status?: string): boolean {
  return !!status && REVOCABLE_LISTING_STATUSES.has(status)
}

export function AssetsPage() {
  const { productionId = '' } = useParams()
  const { t } = useTranslation()
  const [items, setItems] = useState<ProductionAsset[]>([])
  const [tagCloud, setTagCloud] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mediaTab, setMediaTab] = useState<AssetMediaTab>('all')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [listingOpen, setListingOpen] = useState(false)
  const [listingAsset, setListingAsset] = useState<ProductionAsset | null>(null)
  const [listingsByAsset, setListingsByAsset] = useState<Record<string, StudioProductionListing>>({})
  const [busyAssetId, setBusyAssetId] = useState<string | null>(null)
  const [production, setProduction] = useState<Production | null>(null)
  const [uploading, setUploading] = useState(false)
  const [archivedView, setArchivedView] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [assetRes, listingRes, tagRes, prod] = await Promise.all([
        listAssets(productionId, { q: searchQuery || undefined }),
        listProductionListings(productionId).catch(() => ({ items: [] as StudioProductionListing[] })),
        listAssetTags(productionId).catch(() => ({ items: [] as { tag: string; count: number }[] })),
        getProduction(productionId).catch(() => null),
      ])
      setItems(assetRes.items ?? [])
      setTagCloud((tagRes.items ?? []).map((x) => x.tag).filter(isVisibleAssetTag))
      setProduction(prod)
      const map: Record<string, StudioProductionListing> = {}
      for (const l of listingRes.items ?? []) {
        if (l.studio_asset_public_id) {
          map[l.studio_asset_public_id] = l
        }
      }
      setListingsByAsset(map)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, searchQuery])

  useEffect(() => {
    void reload()
  }, [reload])

  const filtered = useMemo(() => {
    return (items ?? []).filter((a) => {
      const archived = a.status === 'archived'
      if (archivedView ? !archived : archived) return false
      if (!assetMatchesMediaTab(a, mediaTab)) return false
      if (selectedTags.length > 0) {
        const tags = a.tags ?? []
        if (!selectedTags.some((sel) => tags.some((x) => x.toLowerCase() === sel.toLowerCase()))) {
          return false
        }
      }
      return true
    })
  }, [items, mediaTab, selectedTags, archivedView])

  const archivedCount = useMemo(() => (items ?? []).filter((a) => a.status === 'archived').length, [items])

  const { page, setPage, total, items: pageItems } = useClientPagination(filtered, ASSETS_PAGE_SIZE)

  useEffect(() => {
    setPage(0)
  }, [searchQuery, mediaTab, selectedTags, archivedView, setPage])

  function openListingModal(asset: ProductionAsset) {
    setListingAsset(asset)
    setListingOpen(true)
  }

  async function markReady(asset: ProductionAsset) {
    setBusyAssetId(asset.id)
    setError(null)
    try {
      await patchProductionAsset(productionId, asset.id, { status: 'ready' })
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.markReadyFailed'))
    } finally {
      setBusyAssetId(null)
    }
  }

  async function unmarkReady(asset: ProductionAsset) {
    setBusyAssetId(asset.id)
    setError(null)
    try {
      await patchProductionAsset(productionId, asset.id, { status: 'draft' })
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.unmarkReadyFailed'))
    } finally {
      setBusyAssetId(null)
    }
  }

  async function archiveAsset(asset: ProductionAsset) {
    setBusyAssetId(asset.id)
    setError(null)
    try {
      await patchProductionAsset(productionId, asset.id, { status: 'archived' })
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.archiveFailed'))
    } finally {
      setBusyAssetId(null)
    }
  }

  async function revokeListing(asset: ProductionAsset) {
    setBusyAssetId(asset.id)
    setError(null)
    try {
      await revokeStudioAssetListing(productionId, asset.id)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.revokeListingFailed'))
    } finally {
      setBusyAssetId(null)
    }
  }

  async function handleUpload(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      await uploadProductionAsset(productionId, file)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.uploadFailed'))
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  async function registerCanvasAsset(asset: ProductionAsset) {
    const url = asset.media_url || asset.url
    if (!url) return
    setBusyAssetId(asset.id)
    setError(null)
    try {
      await registerProductionAsset(productionId, {
        url,
        name: asset.name,
        kind: asset.kind,
        source: 'canvas',
        canvas_node_id: asset.canvas_node_id,
      })
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'assets.registerFailed'))
    } finally {
      setBusyAssetId(null)
    }
  }

  const canManageAssets = production?.is_owner === true || production?.role === 'art_director'

  function renderActions(a: ProductionAsset) {
    if (!isRegisteredAsset(a)) {
      return (
        <div className="mt-2">
          <ProtoBtn
            size="xs"
            variant="soft"
            tone="amber"
            className="w-full"
            disabled={busyAssetId === a.id || !canManageAssets}
            title={t('assets.registerFromCanvas')}
            onClick={(e) => {
              e.stopPropagation()
              void registerCanvasAsset(a)
            }}
          >
            <span className="truncate">{t('assets.registerFromCanvas')}</span>
          </ProtoBtn>
        </div>
      )
    }
    const listing = listingsByAsset[a.id]
    const listingRevocable = isRevocableListing(listing?.status)
    const canUnmarkReady = a.status === 'ready' && !listingRevocable
    const canArchive = a.status === 'ready' && !listingRevocable
    return (
      <div className="mt-2 flex flex-nowrap items-center gap-1">
        {a.status === 'draft' ? (
          <ProtoBtn
            size="xs"
            variant="soft"
            tone="emerald"
            className="min-w-0 flex-1 px-1"
            disabled={busyAssetId === a.id || !canManageAssets}
            title={t('assets.markReady')}
            onClick={(e) => {
              e.stopPropagation()
              void markReady(a)
            }}
          >
            <CheckCircle2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{t('assets.markReady')}</span>
          </ProtoBtn>
        ) : null}
        {canUnmarkReady ? (
          <ProtoBtn
            size="xs"
            variant="soft"
            tone="amber"
            className="min-w-0 flex-1 px-1"
            disabled={busyAssetId === a.id || !canManageAssets}
            title={t('assets.unmarkReady')}
            onClick={(e) => {
              e.stopPropagation()
              void unmarkReady(a)
            }}
          >
            <RotateCcw className="h-3 w-3 shrink-0" />
            <span className="truncate">{t('assets.unmarkReady')}</span>
          </ProtoBtn>
        ) : null}
        {canArchive ? (
          <ProtoBtn
            size="xs"
            variant="soft"
            tone="rose"
            className="min-w-0 flex-1 px-1"
            disabled={busyAssetId === a.id || !canManageAssets}
            title={t('assets.archive')}
            onClick={(e) => {
              e.stopPropagation()
              void archiveAsset(a)
            }}
          >
            <Archive className="h-3 w-3 shrink-0" />
            <span className="truncate">{t('assets.archive')}</span>
          </ProtoBtn>
        ) : null}
        {a.status === 'ready' && !listing ? (
          <ProtoBtn
            size="xs"
            variant="soft"
            tone="emerald"
            className="min-w-0 flex-1 px-1"
            disabled={!canManageAssets}
            title={t('assets.createListing')}
            onClick={(e) => {
              e.stopPropagation()
              openListingModal(a)
            }}
          >
            <Store className="h-3 w-3 shrink-0" />
            <span className="truncate">{t('assets.createListing')}</span>
          </ProtoBtn>
        ) : null}
        {listingRevocable ? (
          <ProtoBtn
            size="xs"
            variant="soft"
            tone="rose"
            className="min-w-0 flex-1 px-1"
            disabled={busyAssetId === a.id || !canManageAssets}
            title={t('assets.revokeListing')}
            onClick={(e) => {
              e.stopPropagation()
              void revokeListing(a)
            }}
          >
            <XCircle className="h-3 w-3 shrink-0" />
            <span className="truncate">{t('assets.revokeListing')}</span>
          </ProtoBtn>
        ) : null}
        {listing ? (
          <span className="min-w-0 flex-1 truncate text-center text-[10px] leading-tight text-zinc-500">
            {t('assets.listingStatus', { status: listing.status })}
          </span>
        ) : null}
      </div>
    )
  }

  const mediaTabs = useMemo(
    () =>
      [
        { id: 'all' as const, label: t('assets.tab.all') },
        { id: 'image' as const, label: t('assets.tab.image') },
        { id: 'video' as const, label: t('assets.tab.video') },
        { id: 'audio' as const, label: t('assets.tab.audio') },
        { id: 'other' as const, label: t('assets.tab.other') },
      ] satisfies { id: AssetMediaTab; label: string }[],
    [t],
  )

  return (
    <CatalogListPageShell>
      <CatalogListToolbar
        actions={
          <div className="flex w-full flex-wrap items-center justify-end gap-2 sm:w-auto">
            <div className="relative min-w-[200px] flex-1 max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <ProtoInput
                className="pl-9"
                placeholder={t('assets.searchPlaceholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <ProtoBtn size="sm" variant="secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {t('assets.upload')}
            </ProtoBtn>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*,audio/*"
              className="hidden"
              onChange={(e) => void handleUpload(e.target.files)}
            />
          </div>
        }
      />

      <ProtoTabs tabs={mediaTabs} active={mediaTab} onChange={setMediaTab} production />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <ProtoChip active={!archivedView} production onClick={() => setArchivedView(false)}>
          {t('assets.viewActive')}
        </ProtoChip>
        <ProtoChip active={archivedView} production onClick={() => setArchivedView(true)}>
          {t('assets.viewArchived', { count: archivedCount })}
        </ProtoChip>
      </div>

      {tagCloud.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {tagCloud.map((tag) => (
            <ProtoChip
              key={tag}
              active={selectedTags.includes(tag)}
              production
              onClick={() =>
                setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]))
              }
            >
              {tag}
            </ProtoChip>
          ))}
        </div>
      ) : null}

      {error ? <p className="mb-4 mt-4 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}

      <div className="mt-4">
        {loading ? (
          <CatalogListSkeleton count={10} />
        ) : filtered.length === 0 ? (
          <ProductionEmptyPanel>
            {archivedView
              ? t('assets.emptyArchived')
              : mediaTab === 'all' && !searchQuery && selectedTags.length === 0
                ? t('assets.empty')
                : t('assets.emptyTab')}
          </ProductionEmptyPanel>
        ) : (
          <>
            <div className={CATALOG_LIST_GRID_CLASS}>
              {pageItems.map((a) => (
                <AssetCard key={a.id} asset={a} actions={renderActions(a)} />
              ))}
            </div>
            <ProtoPagination
              page={page}
              pageSize={ASSETS_PAGE_SIZE}
              total={total}
              onPageChange={setPage}
              prevLabel={t('pagination.prev')}
              nextLabel={t('pagination.next')}
            />
          </>
        )}
      </div>

      <AssetListingModal
        open={listingOpen}
        onClose={() => setListingOpen(false)}
        productionId={productionId}
        asset={listingAsset}
      />
    </CatalogListPageShell>
  )
}
